/** Microsoft Teams connection types, derived wholly from the committed OpenAPI
 *  schema (plan §5a, NG-1096).
 *
 *  Derived rather than hand-written, for the same reason `lms.ts` is: this surface
 *  is brand new, so there is no legacy hand-transcribed shape to half-migrate away
 *  from, and the `API contract fresh` CI job regenerates `api.generated.ts` from the
 *  branch's own serializers and fails on a diff. A hand-copied mirror of
 *  `msteams/serializers.py` would compile happily against an API that had moved.
 *
 *  The one endpoint deliberately absent from the schema is the app package
 *  (`@extend_schema(exclude=True)` — it answers `application/zip`, which the
 *  generator can only call `unknown`), so its download shape is declared here.
 */
import type { components } from "./api.generated";

/** The connection as every Teams connection endpoint returns it.
 *
 *  One response shape whether or not a connection exists: a workspace that has never
 *  enabled Teams gets the same keys with null values and `state: "not_enabled"`,
 *  rather than a 404 or a second shape. Every field is nullable for that case, which
 *  is why the card reads `state` and never infers from `is_enabled`.
 */
export type TTeamsConnection = components["schemas"]["TeamsConnection"];

/** The four states, derived server-side from facts rather than stored.
 *
 *  `not_enabled` — no live row. `disabled` — a row with `is_enabled` false.
 *  `pending_tenant_approval` — enabled, and no install has been seen yet. `enabled` —
 *  enabled, and at least one install has. The Pending → Enabled edge is *observed*:
 *  the customer's Teams admin has to allow our package in their tenant before anyone
 *  can install it, and the first inbound activity carrying the tenant id is the proof.
 */
export type TTeamsConnectionState = components["schemas"]["TeamsConnectionStateEnum"];

/** The sign-in start's body: one key, and it is a URL the browser is sent to. */
export type TTeamsSignInStart = components["schemas"]["TeamsSignInStart"];

/** The app-package download: the bytes plus the name the server chose for them.
 *
 *  The filename is the server's (`cognota-teams-<env label>-<version>.zip`) rather
 *  than a client-side constant on purpose — the label says which environment's bot id
 *  the manifest carries and the version says which build it is, which are the two
 *  questions somebody re-uploading the package six months later has. A hardcoded name
 *  would answer both wrongly.
 */
export interface ITeamsAppPackage {
  blob: Blob;
  filename: string;
}

/** The redirect reasons `msteams_error=<code>` can carry.
 *
 *  Every code the callback emits (`views_connection.TeamsSignInCallbackEndpoint`), in
 *  the order its docstring lists them. `invalid_state` is deliberately coarse — it
 *  covers tampered, expired, missing and workspace-gone alike, because a browser
 *  learning which check it failed is a free oracle.
 *
 *  **One list for both purposes**, because there is one callback endpoint: the same URL
 *  finishes the workspace consent (`enable_workspace`) and a person's own link
 *  (`link_self`, NG-1134), so a code minted for either can land on either surface. The
 *  two readers translate it under their own key prefix — the *recourse* differs — but
 *  neither may narrow the set, or a real failure reaches the user as a missing-key
 *  string. `already_enabled` and `forbidden` only ever arise from the workspace half
 *  and `no_enabled_workspace` only from the personal half; that asymmetry is a
 *  property of the flows, not of what a browser can put in a URL.
 */
export const TEAMS_SIGNIN_ERROR_CODES = [
  "invalid_state",
  "denied",
  "no_code",
  "forbidden",
  "exchange_failed",
  "already_enabled",
  "not_yet",
  "no_enabled_workspace",
] as const;

export type TTeamsSignInErrorCode = (typeof TEAMS_SIGNIN_ERROR_CODES)[number];
